import goToActivity from 'a/actions/navigationActions';
import GameIds from 'a/hub/data/GameIds';
import { GRADES, LECTURA_GRADES } from 'a/hub/data/constants';
import { DESTINATION_GROUP, NAVIGATION_TRIGGER_TYPE } from 'root/lib/SearchlightDispatcher';
import {
  getNewDate,
  getTimestamp,
  getCurrentTimeInMs,
  getCurrentMonth,
  getCurrentYear,
} from 'root/lib/DatetimeManager';
import { PREVENT_SKILL_SCAN_PARAM } from 'a/placementTool/data/constants';
import { getBenchmarkEOYPath } from 'a/placementTool/redux/selectors';
import { PRODUCT_LECTURA } from 'root/lib/constants';

import { GAME_ID, AGGREGATE_ID_ES, ACTIONS } from '../../data/constants';
import { saveSkillScanProgress } from '../../api/apiEvents';
import { reportAggregateScore } from '../../api/reportingEvents';

export const setSkillScanMeasures = ({ ids, measures, grade }) => ({
  gameId: GAME_ID,
  type: ACTIONS.SET_SKILL_SCAN_MEASURES,
  payload: { ids, measures, isMtGSkillScanSentences: grade === GRADES.GRADE_2 },
});

export const setSkillScanActivity = ids => ({
  gameId: GAME_ID,
  type: ACTIONS.SET_SKILL_SCAN_ACTIVITY,
  payload: ids,
});

export const setSkillScanPassage = payload => ({
  gameId: GAME_ID,
  type: ACTIONS.SET_SKILL_SCAN_PASSAGE,
  payload,
});

export const setSkillScanForm = payload => ({
  gameId: GAME_ID,
  type: ACTIONS.SET_SKILL_SCAN_FORM,
  payload,
});

export const setSkillScanSDForm = payload => ({
  gameId: GAME_ID,
  type: ACTIONS.SET_SKILL_SCAN_SD_FORM,
  payload,
});

export const setSkillScanLSCForm = payload => ({
  gameId: GAME_ID,
  type: ACTIONS.SET_SKILL_SCAN_LSC_FORM,
  payload,
});

export const setSkillScanAggregateScore = payload => ({
  gameId: GAME_ID,
  type: ACTIONS.SET_SKILL_SCAN_AGGREGATE_SCORE,
  payload,
});

// for skill_scan_aggregate_es `storyId` is required for grade `KG` only
// for grade `01` growth results will be triggered by writeResult for rwr_es which has storyId
const getAggegateStoryId = (grade, formId) => {
  if (!formId || grade !== GRADES.KINDERGARTEN) {
    return undefined;
  }

  return `${formId}_el`;
};

export const setSkillScanSessionData = (skillScanSessionId, skillScanSessionData) => ({
  gameId: GAME_ID,
  type: ACTIONS.SET_SKILL_SCAN_SESSION_DATA,
  payload: { skillScanSessionId, skillScanSessionData },
});

export const setSkillScanMeasureComplete = () => (dispatch, getState) => {
  const { appSessionReducer: { product }, skillScan } = getState();
  const { measures, measuresRemaining, measuresCompleted } = skillScan;

  const [measureCompleted, ...updatedMeasuresRemaining] = measuresRemaining;
  const updatedMeasuresCompleted = [...measuresCompleted, measureCompleted];

  dispatch({
    gameId: GAME_ID,
    type: ACTIONS.SET_SKILL_SCAN_MEASURE_COMPLETE,
    payload: {
      measuresRemaining: updatedMeasuresRemaining,
      measuresCompleted: updatedMeasuresCompleted,
    },
  });

  if (measures.length !== updatedMeasuresCompleted.length) {
    dispatch(setSkillScanActivity(updatedMeasuresRemaining));
    const { skillScan: savedData } = getState();
    dispatch(saveSkillScanProgress(savedData, product));
  }
};

export const setSkillScanLastTimePlayed = () => ({
  gameId: GAME_ID,
  type: ACTIONS.SET_SKILL_SCAN_LAST_TIME_PLAYED,
  payload: getTimestamp(),
});

export const markSkillScanAsComplete = () => (dispatch, getState) => {
  dispatch(setSkillScanLastTimePlayed());
  dispatch(setSkillScanRedirectFlagHardcoded(false));

  const { appSessionReducer: { product }, skillScan } = getState();
  dispatch(saveSkillScanProgress(skillScan, product));
};

export const completeSkillScan = () => async (dispatch, getState) => {
  const { persistStateReducer: { grade }, appSessionReducer: { product }, skillScan } = getState();
  const { measures, aggregateScoreData, teacherResetInfo } = skillScan;
  const shouldAggregateResults = product === PRODUCT_LECTURA
    && measures.length > 1
    && [GRADES.KINDERGARTEN, GRADES.GRADE_1].includes(grade);

  dispatch(markSkillScanAsComplete());

  if (shouldAggregateResults) {
    const aggregateScorePayload = {
      ...aggregateScoreData,
      storyId: getAggegateStoryId(grade, teacherResetInfo.formID),
    };
    await reportAggregateScore(AGGREGATE_ID_ES, aggregateScorePayload, dispatch);
  }
};

export const goToSkillScan = ({
  origin = '[unknown origin]',
  trigger = '[unknown trigger]',
  triggerType = NAVIGATION_TRIGGER_TYPE.MANUAL,
} = {}) =>
  (dispatch) => {
    const navigationParams = {
      destinationGroup: DESTINATION_GROUP.SKILL_SCAN,
      triggerType,
      trigger,
      origin,
    };
    dispatch(goToActivity({
      activityName: GameIds.SKILL_SCAN,
      isQuestWorkflow: true,
      navigationParams,
    }));
  };

export const setSkillScanRedirectFlag = () => (dispatch, getState) => {
  const state = getState();
  const {
    persistStateReducer,
    appSessionReducer: { product },
    skillScan,
    placementTool: {
      isBenchmarkModeOn,
      metrics: {
        localIPTEndReadingTime = 0,
        localIPTEndLecturaTime = 0,
      } = { localIPTEndReadingTime: 0, localIPTEndLecturaTime: 0 },
    },
    questWorkflowReducer: {
      isQuestInDemoMode,
    },
  } = state;
  const benchmarkEOYPath = getBenchmarkEOYPath(state);
  const currentTimeInMiliseconds = getCurrentTimeInMs();
  const fiveDaysInMiliseconds = 5 * 24 * 60 * 60 * 1000;
  const isLectura = product === PRODUCT_LECTURA;

  // 5 day cool down time between placement and Skill Scan
  const localIPTEndProductTime = isLectura ? localIPTEndLecturaTime : localIPTEndReadingTime;
  if (localIPTEndProductTime
    && (currentTimeInMiliseconds - getNewDate(localIPTEndProductTime)) < fiveDaysInMiliseconds
  ) {
    return;
  }

  const preventSkillScan = isQuestInDemoMode
    || isBenchmarkModeOn // teacher reset TRO only
    || benchmarkEOYPath // RWR + TRO at EOY (not for placement)
    || (isLectura && !LECTURA_GRADES.includes(persistStateReducer.grade)); // Lectura Skill Scan for grades K-2 only

  const shouldRedirect = preventSkillScan ? false : dispatch(getShouldShowSkillScan(persistStateReducer, skillScan));

  dispatch({
    gameId: GAME_ID,
    type: ACTIONS.SET_SKILL_SCAN_REDIRECT_FLAG,
    payload: shouldRedirect,
  });
};

export const setSkillScanRedirectFlagHardcoded = payload => ({
  gameId: GAME_ID,
  type: ACTIONS.SET_SKILL_SCAN_REDIRECT_HARDCODED,
  payload,
});

const clearTeacherResetInfo = () => ({
  gameId: GAME_ID,
  type: ACTIONS.CLEAR_TEACHER_RESET_INFO,
});

const tempDisableSkillScan = false;
export const getShouldShowSkillScan = ({ redirectToIpt, grade }, { lastTimePlayed, teacherResetInfo }) => (dispatch) => {
  if (redirectToIpt) {
    return false;
  }

  const currentYear = getCurrentYear();
  const currentMonth = getCurrentMonth();
  const monthStartDate = new Date(currentYear, currentMonth, 1);
  const isPreventSkillScanParamPresent = new URLSearchParams(window.location.search).get(PREVENT_SKILL_SCAN_PARAM);
  // conditions under which to never show Skill Scan
  const commonDisableConditions = tempDisableSkillScan // DEV flag to turn it off
    || grade === GRADES.PRE_KINDERGARTEN // never for pre-kindergarten
    || [6, 7].includes(currentMonth) // Don't show Skill Scan in July and August
    || isPreventSkillScanParamPresent; // don't show Skill Scan after APT completion

  // if any common conditions met do not serve skill scan - teacher reset lower in hierarchy
  if (commonDisableConditions) {
    return false;
  }

  // if teacher reset present, serve skill scan even if last time taken >= start of month
  if (teacherResetInfo?.teacherReset) {
    return true;
  }

  // no teacher reset and student already served skill scan this month do not serve skill scan
  if (lastTimePlayed && getNewDate(lastTimePlayed) >= monthStartDate) {
    return false;
  }

  // first Skill Scan of the month - clear teacher reset info
  dispatch(clearTeacherResetInfo());
  return true;
};

export const setShouldDisplayAmpGlobalNav = payload => ({
  gameId: GAME_ID,
  type: ACTIONS.SET_SHOULD_DISPLAY_AMP_GLOBAL_NAV,
  payload,
});
